import { useState, useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import { Phone, Menu, X, MessageCircle } from "lucide-react";
import { cityConfig } from "@/config/cityConfig";

import { useSettings } from "@/hooks/useSettings";

const BRIMO_LOGO = "https://customer-assets.emergentagent.com/job_c18fba98-66cc-4e36-b9db-e24324823938/artifacts/t6fqodcb_BRIMO_Logo_Schwarz.png";

const BrimoLogo = () => (
  <Link to="/" className="flex items-center gap-3 group" data-testid="logo-link">
    <img
      src={BRIMO_LOGO}
      alt="BRIMO Immobilien NRW"
      className="h-9 md:h-10 w-auto"
      data-testid="brimo-logo"
    />
    <div className="hidden sm:flex flex-col leading-none">
      <span className="text-white font-bold text-[10px] uppercase tracking-[0.25em]">
        BRICKS &amp; MORTAR
      </span>
      <span className="text-gray-500 text-[9px] uppercase tracking-[0.2em] mt-0.5">
        KREIS METTMANN
      </span>
    </div>
  </Link>
);

const allNavLinks = [
  { label: "Verkaufen", href: "/immobilie-verkaufen-kreis-mettmann" },
  { label: "Kaufen", href: "/immobilie-kaufen-kreis-mettmann" },
  { label: "Marktwert", href: "/marktwertermittlung-kreis-mettmann" },
  { label: "Objekte", href: "/objekte", setting: "objekte_enabled" },
  { label: "Ratgeber", href: "/ratgeber" },
  { label: "Aktuelles", href: "/aktuelles", setting: "aktuelles_enabled" },
  { label: "Kontakt", href: "/kontakt" },
];

export default function Navbar() {
  const [menuOpen, setMenuOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const location = useLocation();
  const settings = useSettings();

  const navLinks = allNavLinks.filter((l) => !l.setting || settings[l.setting] !== false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 50);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    setMenuOpen(false);
  }, [location.pathname]);

  return (
    <nav
      className={`fixed top-0 w-full z-50 transition-all duration-300 border-b border-white/5 ${
        scrolled ? "bg-[#0d0d0d] shadow-2xl" : "bg-[#1a1a1a]/90 backdrop-blur-md"
      }`}
      data-testid="navbar"
    >
      <div className="container mx-auto px-4 md:px-6 max-w-7xl">
        <div className="flex items-center justify-between h-16 md:h-20">
          <BrimoLogo />

          {/* Desktop Nav */}
          <div className="hidden md:flex items-center gap-8">
            {navLinks.map((link) => (
              <Link
                key={link.href}
                to={link.href}
                className={`text-xs uppercase font-bold tracking-[0.2em] transition-colors ${
                  location.pathname === link.href
                    ? "text-[#E61D38]"
                    : "text-gray-300 hover:text-white"
                }`}
                data-testid={`nav-${link.label.toLowerCase()}`}
              >
                {link.label}
              </Link>
            ))}
          </div>

          {/* Right actions */}
          <div className="flex items-center gap-3">
            <a
              href={`tel:${cityConfig.phone}`}
              className="hidden lg:flex items-center gap-2 text-[#E61D38] font-bold text-xs hover:text-white transition-colors uppercase tracking-wider"
              data-testid="navbar-phone"
            >
              <Phone size={13} />
              {cityConfig.phoneDisplay}
            </a>
            <a
              href={`https://wa.me/${cityConfig.whatsapp}?text=Hallo%2C%20ich%20interessiere%20mich%20f%C3%BCr%20eine%20Immobilienberatung.`}
              className="hidden md:flex items-center gap-1.5 bg-[#E61D38] text-white px-4 py-2 text-xs font-bold uppercase tracking-wider hover:bg-[#c41930] transition-colors"
              data-testid="navbar-whatsapp"
              target="_blank"
              rel="noopener noreferrer"
            >
              <MessageCircle size={13} />
              WhatsApp
            </a>
            <button
              onClick={() => setMenuOpen(!menuOpen)}
              className="md:hidden text-white p-2"
              data-testid="mobile-menu-btn"
              aria-label="Menu öffnen"
            >
              {menuOpen ? <X size={22} /> : <Menu size={22} />}
            </button>
          </div>
        </div>

        {/* Mobile Menu */}
        {menuOpen && (
          <div className="md:hidden bg-[#0d0d0d] border-t border-white/5 py-4 pb-16 max-h-[80vh] overflow-y-auto" data-testid="mobile-menu">
            {navLinks.map((link) => (
              <Link
                key={link.href}
                to={link.href}
                className={`block px-4 py-3 text-sm uppercase font-bold tracking-widest transition-colors border-b border-white/5 ${
                  location.pathname === link.href ? "text-[#E61D38]" : "text-gray-300"
                }`}
                data-testid={`mobile-nav-${link.label.toLowerCase()}`}
              >
                {link.label}
              </Link>
            ))}
            <a
              href={`tel:${cityConfig.phone}`}
              className="flex items-center gap-2 px-4 py-3 text-[#E61D38] font-bold text-sm"
              data-testid="mobile-phone"
            >
              <Phone size={14} />
              {cityConfig.phoneDisplay}
            </a>
          </div>
        )}
      </div>
    </nav>
  );
}
